import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Dashboard } from "@smartopt/charts";
import { TABS } from "./tabs";
import { CANVAS_BG, DASHBOARD_THEME } from "./theme";
import { dashboardFetcher, tazele } from "./api";
import "./SmartOptDashboard.css";

/**
 * Power BI embed'in SmartOpt grafik kütüphanesiyle yeniden kurulmuş hâli.
 * Rapordaki gibi altta sekme şeridi var; her sekme kendi config'ini
 * `tabs/` altından getirir, veri yönlendirmesi `api.js`'tedir.
 *
 * Ölçekleme ve TV modu kurgusu plastik fabrikası panosuyla aynı — iki proje
 * arasında davranış farkı olmasın diye bilerek birebir taşındı.
 */
const PAD = 8;
const GAP = 6;
/** Alttaki sekme şeridinin yüksekliği (tuvale bu kadar az yer kalır). */
const TAB_H = 40;

/**
 * Masaüstünde pano sabit genişlikte bir "tasarım tuvaline" çizilir; tuval CSS
 * transform ile bir kez ekrana oturtulur ve tarayıcı zoom'unda BU ÖLÇEK
 * DEĞİŞTİRİLMEZ.
 *
 * Amaç: Ctrl +/- tam olarak trackpad pinch'i gibi davransın. Pinch, layout
 * viewport'a dokunmadığı için sayfayı resim gibi büyütür ve taşan yeri
 * kaydırtır. Tarayıcı zoom'u ise layout viewport'unu küçültüp `resize`
 * tetikler; pano bu yüzden yeniden dizilir, satır yüksekliği ezilirdi.
 */
const DESIGN_W = 1920;
/** Tuvalin inebileceği en kısa boy — çok basık pencerede kartlar ezilmesin. */
const MIN_CANVAS_H = 760;
/**
 * Gerçekten dar cihaz eşiği (fiziksel px). Dar cihazda tuval devreye girmez,
 * kütüphanenin kendi kırılımları (md/sm) çalışır.
 */
const CANVAS_MIN_W = 1024;

/** Tarayıcıların sunduğu standart zoom kademeleri. */
const ZOOM_STEPS = [
  0.25, 0.33, 0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4,
  5,
];

/**
 * Sayfa zoom katsayısı. `outerWidth` zoom'dan etkilenmez, `innerWidth` etkilenir
 * — oranları zoom'u verir. Pencere kenarlığı ve kaydırma çubuğu oranı %1-2
 * saptırdığı için sonuç en yakın standart kademeye yuvarlanır.
 */
const getZoom = () => {
  const raw = window.outerWidth / window.innerWidth;
  if (!isFinite(raw) || raw <= 0) return 1;
  return ZOOM_STEPS.reduce(
    (best, s) => (Math.abs(s - raw) < Math.abs(best - raw) ? s : best),
    1
  );
};

/** API'ler bu aralıkla, sayfa yenilenmeden tazelenir. */
const YENILEME_MS = 30 * 60 * 1000;

/** TV modu ve son sekme tercihi bu tarayıcıda kalıcı olsun diye saklanır. */
const TV_MODE_KEY = "boyahane-pano-tv-mode";
const TAB_KEY = "boyahane-pano-tab";

const SmartOptDashboard = () => {
  const [tabKey, setTabKey] = useState(
    () => localStorage.getItem(TAB_KEY) || TABS[0].key
  );
  const tab = TABS.find((t) => t.key === tabKey) ?? TABS[0];

  const selectTab = (key) => {
    setTabKey(key);
    localStorage.setItem(TAB_KEY, key);
  };

  // TV'ye HDMI ile bağlı ekranlarda büyük font/aralık; laptopta kapalı kalır.
  const [tvMode, setTvMode] = useState(
    () => localStorage.getItem(TV_MODE_KEY) === "1"
  );
  const toggleTvMode = () => {
    setTvMode((prev) => {
      const next = !prev;
      localStorage.setItem(TV_MODE_KEY, next ? "1" : "0");
      return next;
    });
  };

  /**
   * Yenileme: anahtar url'e girer, değişince bütün kartlar yeniden çekilir.
   * Anahtar ancak üç uç arka planda başarıyla geldikten sonra değişir (bkz.
   * api.js `tazele`) — ekran boşalmaz, bir uç düşerse eski veri kalır ve bir
   * sonraki denemede yeniden çekilir.
   */
  const [refreshKey, setRefreshKey] = useState(() => Date.now());
  const [sonGuncelleme, setSonGuncelleme] = useState(() => new Date());
  const [yenilemeHatasi, setYenilemeHatasi] = useState(false);
  const sonDenemeRef = useRef(Date.now());

  useEffect(() => {
    let iptal = false;
    const yenile = async () => {
      sonDenemeRef.current = Date.now();
      const anahtar = Date.now();
      try {
        await tazele(anahtar);
        if (iptal) return;
        setRefreshKey(anahtar);
        setSonGuncelleme(new Date());
        setYenilemeHatasi(false);
      } catch {
        if (!iptal) setYenilemeHatasi(true);
      }
    };

    const interval = setInterval(yenile, YENILEME_MS);
    // Arka plandaki sekmede ve uykuda zamanlayıcı gecikir/donar; ekrana
    // dönüldüğünde süre dolmuşsa beklemeden yenilenir.
    const gorununce = () => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - sonDenemeRef.current >= YENILEME_MS
      ) {
        yenile();
      }
    };
    document.addEventListener("visibilitychange", gorununce);
    window.addEventListener("focus", gorununce);
    return () => {
      iptal = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", gorununce);
      window.removeEventListener("focus", gorununce);
    };
  }, []);

  /**
   * Ölçüm: kullanılabilir FİZİKSEL alan → ölçek ve tuval boyu. Grafiklere
   * piksel yükseklik verilir, pano %100 zoom'da ekrana kaydırmasız oturur.
   */
  const wrapRef = useRef(null);
  // { scale, canvasH, rowPx, canvas } — canvas=false ise dar cihaz yolu.
  const [box, setBox] = useState(null);
  const rows = tab.rows;

  useLayoutEffect(() => {
    const measure = () => {
      const el = wrapRef.current;
      if (!el) return;
      const zoom = getZoom();
      const top = el.getBoundingClientRect().top;
      // clientWidth padding'i içerir (box-sizing: border-box) — dolgu düşülür.
      // CSS px ölçüler zoom'la çarpılıp fiziksel boyuta çevrilir.
      const availW = Math.max(320, (el.clientWidth - PAD * 2) * zoom);
      const availH = Math.max(
        480,
        (window.innerHeight - top - PAD * 2 - TAB_H) * zoom
      );

      let next;
      if (availW < CANVAS_MIN_W) {
        // Dar cihaz: ölçek yok, satır yüksekliği ekrandan.
        const h = window.innerHeight - top - PAD * 2 - TAB_H;
        next = {
          canvas: false,
          scale: 1,
          canvasH: h,
          rowPx: Math.floor((h - (rows - 1) * GAP) / rows),
        };
      } else {
        const scale = availW / DESIGN_W;
        // Tasarım px cinsinden yükseklik: kalan fiziksel boy ölçeğe bölünür.
        const canvasH = Math.max(MIN_CANVAS_H, Math.floor(availH / scale));
        next = {
          canvas: true,
          scale,
          canvasH,
          rowPx: Math.floor((canvasH - (rows - 1) * GAP) / rows),
        };
      }

      // Zoom sırasında ölçüler değişmediği için gereksiz render engellenir.
      setBox((prev) =>
        prev &&
        prev.canvas === next.canvas &&
        prev.rowPx === next.rowPx &&
        prev.canvasH === next.canvasH &&
        Math.abs(prev.scale - next.scale) < 0.001
          ? prev
          : next
      );
    };

    measure();
    // Genişlik değişimi için ResizeObserver (kenar çubuğunun açılıp kapanması
    // gibi pencere dışı sebepleri de yakalar), pencere boyu için resize olayı.
    const ro = new ResizeObserver(measure);
    if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [rows]);

  const config = useMemo(
    () => tab.build(box?.rowPx ?? 0, GAP, refreshKey, tvMode),
    [tab, box?.rowPx, refreshKey, tvMode]
  );

  const dashboard = (
    <Dashboard
      // Slicer durumu Dashboard'un içinde tutuluyor; sekme değişince pano
      // yeniden kurulur ki bir sayfadaki seçim ötekine taşınmasın (raporda da
      // her sayfanın slicer'ı kendine ait).
      key={tab.key}
      className={tvMode ? "pano tv-mode" : "pano"}
      config={config}
      fetcher={dashboardFetcher}
      mode="dark"
      theme={DASHBOARD_THEME}
    />
  );

  return (
    <div
      style={{
        background: CANVAS_BG,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <div
        ref={wrapRef}
        style={{
          flex: 1,
          minHeight: 0,
          padding: PAD,
          boxSizing: "border-box",
          // Zoom'da tuval yatayda taşar; pinch'teki gibi kaydırılabilsin.
          overflowX: "auto",
        }}
      >
        {box?.rowPx > 0 &&
          (box.canvas ? (
            // Dış kutu ölçeklenmiş boyu yer olarak ayırır; transform düzen
            // akışını etkilemediği için bu olmadan altta boşluk oluşurdu.
            <div
              style={{
                width: DESIGN_W * box.scale,
                height: box.canvasH * box.scale,
              }}
            >
              <div
                style={{
                  width: DESIGN_W,
                  height: box.canvasH,
                  transformOrigin: "top left",
                  transform: `scale(${box.scale})`,
                }}
              >
                {dashboard}
              </div>
            </div>
          ) : (
            dashboard
          ))}
      </div>

      {/* Alttaki sekme şeridi — rapordaki sekmelerin karşılığı. */}
      <div className="pano-tabbar" style={{ height: TAB_H }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => selectTab(t.key)}
            aria-pressed={t.key === tab.key}
            className={t.key === tab.key ? "pano-tab pano-tab--on" : "pano-tab"}
          >
            {t.label}
          </button>
        ))}

        {/* Verinin ne zaman çekildiği — panoya bakan herkes görsün. */}
        <span
          className={yenilemeHatasi ? "pano-guncelleme pano-guncelleme--hata" : "pano-guncelleme"}
          title={
            yenilemeHatasi
              ? "Son yenileme denemesi başarısız — ekrandaki veri bu saatte çekildi, yarım saat içinde yeniden denenecek"
              : "Veriler yarım saatte bir, sayfa yenilenmeden güncellenir"
          }
        >
          Son güncelleme{" "}
          {sonGuncelleme.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
          {yenilemeHatasi && " · yenilenemedi"}
        </span>

        <button
          type="button"
          onClick={toggleTvMode}
          title={
            tvMode
              ? "TV modu açık — laptop/masaüstü için kapat"
              : "TV modu kapalı — HDMI ile bağlı büyük ekran için aç"
          }
          className={tvMode ? "pano-tv pano-tv--on" : "pano-tv"}
        >
          TV
        </button>
      </div>
    </div>
  );
};

export default SmartOptDashboard;
